import { css, html, LitElement, type PropertyValues } from "lit";
import { property } from "lit/decorators/property.js";
import { createRef, ref } from "lit/directives/ref.js";
import { classMap } from "lit-html/directives/class-map.js";

export class SidebarComponent extends LitElement {
	@property({ type: Boolean, reflect: true, attribute: "open" })
	_isOpen = false;

	// no-op
	@property({ type: String, attribute: "origin-position" })
	originPosition: "left" | "right" = "right";

	@property({ type: Boolean, attribute: "close-on-escape" })
	closeOnEscape = false;

	@property({ type: Boolean, attribute: "close-on-backdrop" })
	closeOnBackdrop = false;

	@property({ type: Boolean, attribute: "block-scroll" })
	blockScroll = false;

	@property({ type: String, reflect: true, attribute: "aria-hidden" })
	_ariaHidden = "true";

	@property({ type: String, reflect: true, attribute: "aria-expanded" })
	_ariaExpanded = "false";

	private readonly _backdropRef = createRef<HTMLElement>();
	private readonly _dialogRef = createRef<HTMLDialogElement>();
	private readonly _contentRef = createRef<HTMLElement>();

	protected override firstUpdated(_changedProperties: PropertyValues) {
		super.firstUpdated(_changedProperties);

		if (this.closeOnEscape) {
			globalThis.addEventListener("keydown", (e) => {
				if (e.key === "Escape" && this._isOpen) {
					this.hide();
				}
			});
		}

		if (this.closeOnBackdrop) {
			this._backdropRef.value?.addEventListener("click", () => {
				this.hide();
			});
		}

		this.addEventListener("close", () => {
			this.hide();
		});
	}

	protected override updated(_changedProperties: PropertyValues<SidebarComponent>) {
		super.updated(_changedProperties);

		if (_changedProperties.has("_isOpen")) {
			const isOpen = this._isOpen;
			this._ariaHidden = isOpen ? "false" : "true";
			this._ariaExpanded = isOpen ? "true" : "false";

			if (isOpen && this.blockScroll) {
				document.body.style.overflow = "hidden";
			} else {
				document.body.style.overflow = "";
			}

			if (isOpen) {
				this.dispatchEvent(new CustomEvent("opened"));
			} else {
				this.dispatchEvent(new CustomEvent("closed"));
			}
		}
	}

	public show(): void {
		this._isOpen = true;
		document.body.append(this);
	}
	public hide(): void {
		this._isOpen = false;
	}
	public isOpen(): boolean {
		return this._isOpen;
	}

	protected override render() {
		return html`
      <dialog ${ref(this._dialogRef)} ?open="${this._isOpen}" aria-live="polite">
        <div class="backdrop" ${ref(this._backdropRef)}></div>
        <main
          ${ref(this._contentRef)}
          class="${classMap({
						"origin-right": this.originPosition === "right",
						"origin-left": this.originPosition === "left",
					})}"
        >
          <slot></slot>
        </main>
      </dialog>
    `;
	}

	static override get styles() {
		return [
			css`
        dialog[open] {
          all: unset;
          position: fixed;
          inset: 0;
          z-index: 99999;
          display: grid;
        }

        .backdrop {
          position: fixed;
          background-color: rgba(0, 0, 0, 0.5);
          inset: 0;
        }

        main {
          position: relative;
          z-index: 1;
        }
      `,
		];
	}
}

export function defineSidebarComponent() {
	if (!customElements.get("y-sidebar")) {
		customElements.define("y-sidebar", SidebarComponent);
	}
}
